import { coerceEnumValue } from "@peekie/case-spec";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  ArraySortPathProvider,
  ItemOrderControls,
  SortFieldMark,
} from "./array-sort-ui";
import { MediaUrlField } from "./media-url-field";
import { asArray, isRecord, str } from "./shared";
import {
  appendAtPath,
  childPath,
  getAtPath,
  moveAtIndex,
  removeAtIndex,
  setAtPath,
} from "./path-utils";

export { ArraySortPathProvider, ItemOrderControls, SortFieldMark };

export { MediaUrlField };

export function PeopleSelect({
  id,
  label,
  fieldKey,
  value,
  names,
  disabled,
  allowEmpty,
  onChange,
}: {
  id?: string;
  label: string;
  fieldKey?: string;
  value: string;
  names: string[];
  disabled?: boolean;
  allowEmpty?: boolean;
  onChange: (next: string) => void;
}) {
  const options = [...names];
  if (value && !options.includes(value)) options.unshift(value);
  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        <SortFieldMark fieldKey={fieldKey} />
      </FieldLabel>
      <select
        id={id}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-7 w-full rounded-md border border-input bg-input/20 px-2 text-sm outline-none",
          "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {allowEmpty || !value ? <option value="">（未选）</option> : null}
        {options.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function EnumSelect({
  id,
  label,
  value,
  options,
  optionLabels,
  fieldKey,
  disabled,
  onChange,
}: {
  id?: string;
  label: string;
  value: string;
  options: string[];
  optionLabels?: Record<string, string>;
  fieldKey?: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  const choices = options.filter((opt) => opt.length > 0);
  const selected = choices.includes(value)
    ? value
    : coerceEnumValue(value, choices, fieldKey);
  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        <SortFieldMark fieldKey={fieldKey} />
      </FieldLabel>
      <select
        id={id}
        disabled={disabled}
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-7 w-full rounded-md border border-input bg-input/20 px-2 text-sm outline-none",
          "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
          "disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {choices.map((opt) => (
          <option key={opt} value={opt}>
            {optionLabels?.[opt] ?? opt}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function TextField({
  id,
  label,
  fieldKey,
  value,
  disabled,
  multiline,
  rows,
  placeholder,
  onChange,
}: {
  id?: string;
  label: string;
  fieldKey?: string;
  value: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  onChange: (next: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        <SortFieldMark fieldKey={fieldKey} />
      </FieldLabel>
      {multiline ? (
        <Textarea
          id={id}
          value={value}
          disabled={disabled}
          rows={rows ?? 3}
          placeholder={placeholder}
          className="min-h-16 resize-y text-sm"
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={id}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

export function BoolField({
  id,
  label,
  fieldKey,
  value,
  disabled,
  onChange,
}: {
  id?: string;
  label: string;
  fieldKey?: string;
  value: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <Field orientation="horizontal" className="items-center justify-between">
      <FieldLabel htmlFor={id}>
        {label}
        <SortFieldMark fieldKey={fieldKey} />
      </FieldLabel>
      <Switch
        id={id}
        checked={value}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </Field>
  );
}

/** Bubble list: [{ body: string }, ...] */
export function BubbleListEditor({
  root,
  path,
  label,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  path: string;
  label: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const items = asArray(getAtPath(root, path));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium">{label}</p>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={disabled}
          onClick={() =>
            onChangeRoot(appendAtPath(root, path, { body: "" }))
          }
        >
          添加气泡
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无气泡</p>
      ) : (
        items.map((item, i) => {
          const body = isRecord(item) ? str(item.body) : str(item);
          return (
            <div key={i} className="flex gap-2">
              <Textarea
                value={body}
                disabled={disabled}
                rows={2}
                className="min-h-12 flex-1 resize-y text-sm"
                onChange={(e) => {
                  if (isRecord(item)) {
                    onChangeRoot(
                      setAtPath(root, childPath(path, i), {
                        ...item,
                        body: e.target.value,
                      }),
                    );
                  } else {
                    onChangeRoot(
                      setAtPath(root, childPath(path, i), {
                        body: e.target.value,
                      }),
                    );
                  }
                }}
              />
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                disabled={disabled}
                className="shrink-0 text-destructive"
                onClick={() => onChangeRoot(removeAtIndex(root, path, i))}
              >
                ✕
              </Button>
            </div>
          );
        })
      )}
    </div>
  );
}

/** Ordered list of media URLs with upload support per row. */
export function MediaUrlListField({
  root,
  path,
  label,
  disabled,
  fieldKey,
  mediaKind = "image",
  onChangeRoot,
}: {
  root: unknown;
  path: string;
  label: string;
  disabled?: boolean;
  fieldKey?: string;
  mediaKind?: "image" | "audio" | "video";
  onChangeRoot: (next: unknown) => void;
}) {
  const items = asArray(getAtPath(root, path)).map((item) => str(item));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium">
          {label}
          <span className="ml-1.5 text-[0.65rem] font-normal text-muted-foreground">
            {items.length} 张
          </span>
        </p>
        <Button
          type="button"
          size="xs"
          variant="outline"
          disabled={disabled}
          onClick={() => onChangeRoot(appendAtPath(root, path, ""))}
        >
          添加图片
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">暂无图片；可添加多张，手机端横滑浏览。</p>
      ) : (
        items.map((url, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <MediaUrlField
                label={`${label} ${i + 1}`}
                value={url}
                disabled={disabled}
                fieldKey={fieldKey}
                mediaKind={mediaKind}
                onChange={(v) =>
                  onChangeRoot(setAtPath(root, childPath(path, i), v))
                }
              />
            </div>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              disabled={disabled || i === 0}
              title="上移"
              className="mt-6 shrink-0"
              onClick={() => onChangeRoot(moveAtIndex(root, path, i, -1))}
            >
              ↑
            </Button>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              disabled={disabled || i >= items.length - 1}
              title="下移"
              className="mt-6 shrink-0"
              onClick={() => onChangeRoot(moveAtIndex(root, path, i, 1))}
            >
              ↓
            </Button>
            <Button
              type="button"
              size="icon-xs"
              variant="ghost"
              disabled={disabled}
              title="删除"
              className="mt-6 shrink-0 text-destructive hover:text-destructive"
              onClick={() => onChangeRoot(removeAtIndex(root, path, i))}
            >
              ✕
            </Button>
          </div>
        ))
      )}
    </div>
  );
}

/** String array as multiline textarea (one item per line). */
export function StringListField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Textarea
        value={value.join("\n")}
        disabled={disabled}
        rows={Math.min(8, Math.max(3, value.length + 1))}
        className="min-h-16 resize-y text-sm"
        onChange={(e) => onChange(e.target.value.split("\n"))}
      />
      <p className="text-[0.65rem] text-muted-foreground">每行一项</p>
    </Field>
  );
}

export function ArrayShell({
  title,
  count,
  disabled,
  onAdd,
  children,
  emptyText = "暂无条目，点击「添加」新建。",
}: {
  title: string;
  count: number;
  disabled?: boolean;
  onAdd: () => void;
  children: ReactNode;
  emptyText?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {title}
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">
            {count} 条
          </span>
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={onAdd}
        >
          添加
        </Button>
      </div>
      {count === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {emptyText}
        </p>
      ) : (
        children
      )}
    </div>
  );
}

export function RowCard({
  title,
  open,
  onToggle,
  disabled,
  index,
  total,
  arrayPath,
  root,
  onChangeRoot,
  confirmDeleteLabel,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
  index: number;
  total: number;
  arrayPath: string;
  root: unknown;
  onChangeRoot: (next: unknown) => void;
  confirmDeleteLabel?: string;
  children: ReactNode;
}) {
  return (
    <li className="rounded-md border border-border bg-card">
      <div className="flex items-center gap-1 border-b border-border/60 px-2 py-1">
        <button
          type="button"
          className="min-w-0 flex-1 truncate rounded-sm px-1 py-0.5 text-left text-xs font-medium hover:bg-muted"
          onClick={onToggle}
        >
          <span className="mr-1 text-muted-foreground">{open ? "▾" : "▸"}</span>
          {title}
        </button>
        <ItemOrderControls
          disabled={disabled}
          index={index}
          total={total}
          arrayPath={arrayPath}
          onMove={(delta) =>
            onChangeRoot(moveAtIndex(root, arrayPath, index, delta))
          }
        />
        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          disabled={disabled}
          title="删除"
          className="text-destructive hover:text-destructive"
          onClick={() => {
            if (
              !window.confirm(
                `删除「${confirmDeleteLabel ?? title}」？`,
              )
            ) {
              return;
            }
            onChangeRoot(removeAtIndex(root, arrayPath, index));
          }}
        >
          ✕
        </Button>
      </div>
      {open ? (
        <ArraySortPathProvider arrayPath={arrayPath}>
          <div className="flex flex-col gap-3 p-3">{children}</div>
        </ArraySortPathProvider>
      ) : null}
    </li>
  );
}

export function useOpenIndex(length: number) {
  const [openIndex, setOpenIndex] = useState<number | null>(
    length > 0 ? 0 : null,
  );
  return {
    openIndex,
    setOpenIndex,
    toggle: (i: number) => setOpenIndex((cur) => (cur === i ? null : i)),
  };
}
